import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { adminApi } from "@/lib/api";
import type { TeamMember } from "@/lib/types";

const roles = [
  { value: "owner", label: "Owner" },
  { value: "sales_manager", label: "Sales Manager" },
  { value: "sales_rep", label: "Sales Rep" },
  { value: "service_technician", label: "Service Technician" },
];

const emptyForm = {
  name: "",
  phone: "",
  telegram_chat_id: "",
  telegram_username: "",
  role: "sales_rep",
  pin: "",
  active: "true",
};

export function TeamPage(): import("react").ReactElement {
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [form, setForm] = useState<Record<string, string>>(emptyForm);
  const queryClient = useQueryClient();

  const members = useQuery({
    queryKey: ["team-members", query],
    queryFn: () => adminApi.listTeamMembers(query),
  });

  const save = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = Object.fromEntries(Object.entries(form).filter(([, value]) => value !== ""));
      payload.active = form.active === "true";
      if (editing) return adminApi.updateTeamMember(editing.id, payload);
      return adminApi.createTeamMember(payload);
    },
    onSuccess: async () => {
      setEditing(null);
      setForm(emptyForm);
      await queryClient.invalidateQueries({ queryKey: ["team-members"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminApi.deleteTeamMember(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["team-members"] });
    },
  });

  function startEdit(member: TeamMember): void {
    setEditing(member);
    setForm({
      name: member.name,
      phone: member.phone ?? "",
      telegram_chat_id: member.telegram_chat_id ?? "",
      telegram_username: member.telegram_username ?? "",
      role: member.role,
      pin: "",
      active: member.active ? "true" : "false",
    });
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    save.mutate();
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_380px]">
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Team</h1>
            <p className="text-sm text-muted-foreground">Admin-created users for Telegram CRM access and portal operations.</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search team" value={query} onChange={(event) => setQuery(event.target.value)} />
          </div>
        </div>

        <Card>
          <CardContent className="overflow-x-auto p-0">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="border-b bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Telegram Chat ID</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.data?.rows.map((member) => (
                  <tr key={member.id} className="border-b last:border-0">
                    <td className="px-4 py-3 font-medium">{member.name}</td>
                    <td className="px-4 py-3">{member.role.replace(/_/g, " ")}</td>
                    <td className="px-4 py-3">{member.phone ?? ""}</td>
                    <td className="px-4 py-3">{member.telegram_chat_id ?? ""}</td>
                    <td className="px-4 py-3">{member.active ? "Active" : "Inactive"}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => startEdit(member)}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" onClick={() => remove.mutate(member.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {members.data?.rows.length === 0 && (
                  <tr>
                    <td className="px-4 py-10 text-center text-muted-foreground" colSpan={6}>
                      No team members yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <Card className="h-fit">
        <CardHeader>
          <CardTitle>{editing ? "Edit Team Member" : "New Team Member"}</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <label className="block space-y-2 text-sm font-medium">
              <span>Name *</span>
              <Input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
            </label>
            <label className="block space-y-2 text-sm font-medium">
              <span>Phone</span>
              <Input value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} />
            </label>
            <label className="block space-y-2 text-sm font-medium">
              <span>Telegram Chat ID</span>
              <Input value={form.telegram_chat_id} onChange={(event) => setForm((current) => ({ ...current, telegram_chat_id: event.target.value }))} />
            </label>
            <label className="block space-y-2 text-sm font-medium">
              <span>Telegram Username</span>
              <Input value={form.telegram_username} onChange={(event) => setForm((current) => ({ ...current, telegram_username: event.target.value }))} />
            </label>
            <label className="block space-y-2 text-sm font-medium">
              <span>Role</span>
              <select
                className="h-9 w-full rounded-md border bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                value={form.role}
                onChange={(event) => setForm((current) => ({ ...current, role: event.target.value }))}
              >
                {roles.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}
              </select>
            </label>
            <label className="block space-y-2 text-sm font-medium">
              <span>Login PIN{editing ? " (leave blank to keep current)" : ""}</span>
              <Input
                inputMode="numeric"
                maxLength={4}
                pattern="[0-9]{4}"
                type="password"
                value={form.pin}
                onChange={(event) => setForm((current) => ({ ...current, pin: event.target.value.replace(/\D/g, "").slice(0, 4) }))}
              />
            </label>
            <label className="block space-y-2 text-sm font-medium">
              <span>Status</span>
              <select
                className="h-9 w-full rounded-md border bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                value={form.active}
                onChange={(event) => setForm((current) => ({ ...current, active: event.target.value }))}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </label>
            {save.error && <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{save.error.message}</p>}
            <div className="flex gap-2">
              <Button disabled={save.isPending}>
                <Plus className="h-4 w-4" />
                {editing ? "Save" : "Create"}
              </Button>
              {editing && <Button type="button" variant="secondary" onClick={() => { setEditing(null); setForm(emptyForm); }}>Cancel</Button>}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
